import React, { useEffect, useState } from "react";
import axiosClient from "../../axios";
import SideBar from "../SideBar";
import "../../assets/css/AdminCommon.css";
import DateSelector from "../../helperComponent/DateSelector";
import CohortKPICards from "./CohortKPICards";
import UserEaringChart from "./UserEaringChart";
import ActivationChart from "./ActivationChart";
import WithdrawalMetricsCard from "./WithdrawalMetricsCard";
import DayWiseTable from "./DayWiseTable";
import CohortComparison from "./CohortComparison";
import OffersEngagementChart from "./OffersEngagementChart";
import RangeTabs from "./RangeTabs";
import UsersEarningDayPanel from "./UsersEarningDayPanel";

const getDayOffersDetail = (dayWiseData, day) => {
	const d = (dayWiseData ?? []).find((x) => x.day === day);
	return {
		categories: d?.offers?.categories ?? [],
		topOffers: d?.offers?.top_offers ?? [],
	};
};

const UserCohortAnalytics = () => {
	const [mode, setMode] = useState("single"); // "single" | "range" | "compare"
	const [selectedDate, setSelectedDate] = useState(null); // Start with null
	const [rangeStartDate, setRangeStartDate] = useState(null);
	const [rangeEndDate, setRangeEndDate] = useState(null);
	const [cohortData, setCohortData] = useState(null);
	const [partners, setPartners] = useState({});
	const [loading, setLoading] = useState(false);

	// Fetch partner mapping on mount
	useEffect(() => {
		fetchPartners();
	}, []);

	// Fetch cohort data when date is selected (single)
	useEffect(() => {
		if (mode !== "single") return;
		if (selectedDate) {
			fetchCohortAnalytics({ s_date: selectedDate });
		}
	}, [mode, selectedDate]);

	// Fetch cohort data when date range is selected (range)
	useEffect(() => {
		if (mode !== "range") return;
		if (rangeStartDate && rangeEndDate) {
			fetchCohortAnalytics({ s_date: rangeStartDate, e_date: rangeEndDate });
		}
	}, [mode, rangeStartDate, rangeEndDate]);

	const fetchPartners = () => {
		axiosClient
			.get("/partnersList")
			.then((res) => {
				const partnerMap = {};
				res?.data?.response?.forEach(p => {
					partnerMap[p._id] = { name: p.name, image: p.image };
				});
				setPartners(partnerMap);
			})
			.catch((err) => {
				console.log("Error fetching partners", err);
			});
	};

	useEffect(() => {
		// clear previous results when mode changes or dates cleared
		setCohortData(null);
	}, [mode]);

	const fetchCohortAnalytics = ({ s_date, e_date } = {}) => {
		if (!s_date) return;

		setLoading(true);
		const sDateIso = `${s_date}T00:00:00.000Z`;
		const eDateIso = e_date ? `${e_date}T00:00:00.000Z` : null;
		const url = eDateIso
			? `/admin/user-summary?s_date=${sDateIso}&e_date=${eDateIso}`
			: `/admin/user-summary?s_date=${sDateIso}`;

		axiosClient
			.get(url)
			.then((res) => {
				setCohortData(res?.data?.response || null);
			})
			.catch((err) => {
				console.log("Error fetching cohort analytics", err);
				alert("Error fetching cohort data");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<>
			<SideBar handleSideBar={true} activeTab={"userAnalytics"} />
			<div className="admin-screen-v4">
				{/* HEADER */}
				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<div className="admin-header-v4">
						<div>
							<h2>User Cohort Analytics</h2>
							<p style={{ margin: "8px 0 0 0", color: "var(--text-muted)", fontSize: "14px" }}>
								{mode === "single"
									? (selectedDate
										? `Analyzing cohort registered on ${selectedDate}`
										: "Select a registration date to analyze user cohort performance")
									: mode === "range"
										? ((rangeStartDate && rangeEndDate)
											? `Analyzing cohorts registered from ${rangeStartDate} to ${rangeEndDate}`
											: "Select a date range to analyze user cohort performance")
										: "Compare cohorts across multiple registration dates"}
							</p>
						</div>
						<div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
							{/* Mode toggle */}
							<div style={{
								display: "flex",
								background: "var(--bg-secondary, #F3F4F6)",
								border: "1px solid var(--border-color, #E5E7EB)",
								borderRadius: "8px",
								padding: "3px",
								gap: "2px",
							}}>
								{["single", "range", "compare"].map((m) => (
									<button
										key={m}
										onClick={() => setMode(m)}
										style={{
											padding: "6px 14px",
											border: "none",
											borderRadius: "6px",
											fontSize: "13px",
											fontWeight: 600,
											cursor: "pointer",
											transition: "all 0.15s",
											background: mode === m ? "var(--bg-card, #fff)" : "transparent",
											color: mode === m ? "var(--primary, #3B82F6)" : "var(--text-muted)",
											boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
										}}
									>
										{m === "single"
											? "Single Cohort"
											: m === "range"
												? "Cohort Range"
												: "Compare Cohorts"}
									</button>
								))}
							</div>

							{mode === "single" && (
								<DateSelector selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
							)}

							{mode === "range" && (
								<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
									<div style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "10px 16px",
										backgroundColor: "white",
										border: "2px solid #E5E7EB",
										borderRadius: "8px",
									}}>
										<span style={{ fontSize: "16px" }}>📅</span>
										<input
											type="date"
											value={rangeStartDate || ""}
											onChange={(e) => setRangeStartDate(e.target.value)}
											max={new Date().toISOString().split("T")[0]}
											style={{
												border: "none",
												outline: "none",
												fontSize: "14px",
												fontWeight: "600",
												color: "#1F2937",
												cursor: "pointer",
											}}
										/>
									</div>

									<span style={{ color: "var(--text-muted)", fontWeight: 700 }}>to</span>

									<div style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										padding: "10px 16px",
										backgroundColor: "white",
										border: "2px solid #E5E7EB",
										borderRadius: "8px",
									}}>
										<span style={{ fontSize: "16px" }}>📅</span>
										<input
											type="date"
											value={rangeEndDate || ""}
											onChange={(e) => setRangeEndDate(e.target.value)}
											max={new Date().toISOString().split("T")[0]}
											min={rangeStartDate || undefined}
											style={{
												border: "none",
												outline: "none",
												fontSize: "14px",
												fontWeight: "600",
												color: "#1F2937",
												cursor: "pointer",
											}}
										/>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{mode === "compare" ? (
					<CohortComparison partners={partners} />
				) : (
				<>
				{mode === "single" && !selectedDate ? (
					// EMPTY STATE: No date selected
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📅</div>
						<h3>Select a Date to Begin</h3>
						<p>Choose a registration date to view cohort analytics (D0-D15)</p>
					</div>
				) : mode === "range" && (!rangeStartDate || !rangeEndDate) ? (
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📅</div>
						<h3>Select a Date Range to Begin</h3>
						<p>Choose a start and end registration date to view analytics</p>
					</div>
				) : loading ? (
					// LOADING STATE
					<div className="loader-container-v4">
						<div className="loader-image-v4">📊</div>
						<p style={{ marginTop: "var(--spacing-md)", color: "var(--text-muted)" }}>
							Loading cohort analytics...
						</p>
					</div>
				) : cohortData ? (
					<>
						{mode === "range" && cohortData?.aggregate ? (
							<RangeTabs
								cohortData={cohortData}
								partners={partners}
								rangeStartDate={rangeStartDate}
								rangeEndDate={rangeEndDate}
							/>
						) : (
							<>
								{/* LEVEL 1: KPI CARDS */}
								<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
									<CohortKPICards summary={cohortData.summary} />
								</div>

								{cohortData?.summary?.usersEarning?.length > 0 && (
									<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
										<h2 style={{ margin: "0 0 12px 0" }}>Earning Wise User (summary)</h2>
										<UserEaringChart summary={cohortData.summary.usersEarning} />
									</div>
								)}

								{/* Day-wise partner earning buckets (cumulative) */}
								{(cohortData.day_wise_data ?? []).some((d) => d?.usersEarning?.length > 0) && (
									<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
										<UsersEarningDayPanel
											dayWiseData={cohortData.day_wise_data}
											totalUsers={cohortData.summary?.total_users}
										/>
									</div>
								)}

								{/* LEVEL 2: ACTIVATION CHART */}
								<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
									<ActivationChart dayWiseData={cohortData.day_wise_data} />
								</div>

								{/* LEVEL 3: WITHDRAWAL METRICS */}
								<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
									<WithdrawalMetricsCard
										summary={cohortData.summary}
										dayWiseData={cohortData.day_wise_data}
									/>
								</div>

								{/* LEVEL 3b: OFFERS ENGAGEMENT TREND + DRILL-DOWN */}
								{cohortData.summary?.offers_engagement_by_day?.length > 0 && (
									<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
										<h3 style={{ margin: "0 0 10px 0" }}>Offers Engagement Trend (D0-D15)</h3>
										<p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, marginTop: 0 }}>
											Offer completion rate (%), day-over-day.
										</p>
										<OffersEngagementChart
											seriesA={{ label: "Cohort", color: "#3B82F6", data: cohortData.summary.offers_engagement_by_day }}
											getDetailA={(day) => getDayOffersDetail(cohortData.day_wise_data, day)}
										/>
									</div>
								)}

								{/* LEVEL 4: DAY-WISE TABLE */}
								<div className="admin-form-container-v4">
									<DayWiseTable
										dayWiseData={cohortData.day_wise_data}
										partners={partners}
										totalUsers={cohortData.summary?.total_users}
									/>
								</div>
							</>
						)}
					</>
				) : (
					// EMPTY STATE: No data
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📭</div>
						<h3>No Data Available</h3>
						<p>No users registered on {selectedDate}</p>
					</div>
				)}
				</>
				)}
			</div>
		</>
	);
};

export default UserCohortAnalytics;
